import React, { useRef, useState } from 'react';
import { Overlay } from '@fiverr-private/overlay';
import { Portal } from '@fiverr-private/ui_utils';
import { Avatar, AvatarImage } from '@fiverr-private/avatar';
import { VettedProBadge } from '@fiverr-private/badges';
import { Button } from '@fiverr-private/button';
import { Rating } from '@fiverr-private/data_display';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Text } from '@fiverr-private/typography';
import { Video } from '@fiverr-private/media';
import { TimesIcon, SoundMinIcon, SoundMutedIcon, PauseSolidIcon, PlaySolidIcon } from '@fiverr-private/visuals';
import { translate } from '../../../shared/helpers';
import { sendMixpanelEvent } from '../utils/mixpanel';
import { MIXPANEL_EVENTS } from '../utils/eventNames';
import { TestimonialItem } from './constants';
import * as styles from './styles.ve.css';

interface TestimonialModalProps {
  testimonial: TestimonialItem;
  isOpen: boolean;
  onClose: () => void;
}

const TestimonialModal: React.FC<TestimonialModalProps> = ({ testimonial, isOpen, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleCtaClick = () => {
    sendMixpanelEvent({
      eventName: MIXPANEL_EVENTS.CLICKED_UGC_POPUP_CTA,
      clickedElement: testimonial.name,
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <Overlay
        onClick={onClose}
        lockBodyScroll
        display="flex"
        justifyContent="center"
        alignItems={{ default: 'flexEnd', md: 'center' }}
      >
        <Stack
          className={styles.modalContainer}
          backgroundColor="white"
          gap={{ default: '8', md: '10' }}
          direction={{ default: 'column', md: 'row' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Container className={styles.closeButton} onClick={onClose}>
            <TimesIcon size="lg" color="black" />
          </Container>

          <Container
            position="relative"
            flexShrink="0"
            height={{ default: '344px', md: '600px' }}
            width={{ default: '100%', md: '411px' }}
          >
            <Video
              ref={videoRef}
              sources={[
                {
                  src: testimonial.video,
                  type: 'video/mp4',
                },
              ]}
              borderRadius="2xl"
              height="100%"
              width="100%"
              controls={false}
              muted={isMuted}
              autoPlay
            />

            <Stack gap="2" className={styles.videoControls}>
              <Container className={styles.videoControlButton} onClick={togglePlay}>
                {isPlaying ? <PauseSolidIcon size="sm" color="black" /> : <PlaySolidIcon size="sm" color="black" />}
              </Container>

              <Container className={styles.videoControlButton} onClick={toggleMute}>
                {isMuted ? <SoundMutedIcon size="sm" color="black" /> : <SoundMinIcon size="sm" color="black" />}
              </Container>
            </Stack>
          </Container>

          <Container>
            <Text className={styles.modalTitleText}>{translate(testimonial.infoTitle)}</Text>

            <Text className={styles.modalDescriptionText}>{translate(testimonial.infoDescription)}</Text>

            <Button
              href={testimonial.infoCtaHref}
              target="_blank"
              className={styles.infoCtaButton}
              onClick={handleCtaClick}
            >
              {translate('v17.testimonials.info_cta')}
            </Button>

            <Text size="b_lg" fontWeight="semibold" marginBottom="4" className={styles.topPicksText}>
              {translate('v17.testimonials.top_picks')}
            </Text>

            <Container display="grid" gap="3" className={styles.sellersGrid}>
              {testimonial.sellers.map((seller, index) => (
                <Container
                  key={index}
                  as="a"
                  href={seller.href}
                  target="_blank"
                  display="flex"
                  padding="3"
                  borderRadius="xl"
                  borderColor="grey_300"
                  borderWidth="sm"
                  gap="2"
                  boxShadow="z1"
                  width="100%"
                  className={styles.sellerCard}
                >
                  <Avatar size="md" username={seller.name} shape={seller.isAgency ? 'square' : 'circle'}>
                    <AvatarImage htmlWidth="56px" htmlHeight="56px" alt={seller.name} src={seller.image} />
                  </Avatar>

                  <Stack direction="column" gap="0.5">
                    <Stack gap="2" alignItems="center">
                      <Text fontWeight="bold" size="b_md" color="bodyPrimary" className={styles.sellerNameText}>
                        {seller.name}
                      </Text>
                      <Rating variant="compact" score={seller.rating} />
                      {seller.isBadgeTop && seller.isPro && <VettedProBadge variant="short" />}
                    </Stack>

                    <Stack gap="1.5" alignItems="center">
                      <Text size="b_sm" className={styles.sellerPositionText}>
                        {seller.position}
                      </Text>
                      {!seller.isBadgeTop && seller.isPro && <VettedProBadge variant="short" />}
                    </Stack>
                  </Stack>
                </Container>
              ))}
            </Container>
          </Container>
        </Stack>
      </Overlay>
    </Portal>
  );
};

export default TestimonialModal;
